/**
 * Delete Admin Level Dialog Component
 * 
 * Confirmation dialog for deleting admin levels.
 */

import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";
import type { AdminLevelItem } from "@/services/adminService";

interface DeleteAdminLevelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  adminLevel: AdminLevelItem | null;
  onConfirm: () => Promise<void>;
}

export function DeleteAdminLevelDialog({
  open,
  onOpenChange,
  adminLevel,
  onConfirm,
}: DeleteAdminLevelDialogProps) {
  const [deleting, setDeleting] = React.useState(false);

  const handleConfirm = async () => {
    if (!adminLevel) return;

    setDeleting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      console.error("Error deleting admin level:", error);
    } finally {
      setDeleting(false);
    }
  };

  if (!adminLevel) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Hapus Admin Level?</AlertDialogTitle>
          <AlertDialogDescription>
            Apakah Anda yakin ingin menghapus admin level <strong>{adminLevel.name}</strong>?
            Tindakan ini tidak dapat dibatalkan.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Batal</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Hapus
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

